import { ReadonlyVec2, vec2 } from 'gl-matrix';

/**
 * Compute the signed area of a polygon using the shoelace
 * (trapezoid) formula. Returns positive for CW winding and
 * negative for CCW winding in Y-up coordinate space.
 */
export function computeSignedArea(polygon: ReadonlyVec2[]): number {
    const n = polygon.length;
    if (n < 3) {
        return 0;
    }
    let sum = 0;
    for (let i = 1; i < n; i++) {
        sum +=
            (polygon[i][0] - polygon[i - 1][0]) *
            (polygon[i][1] + polygon[i - 1][1]);
    }
    // Close the loop: last vertex -> first vertex
    sum +=
        (polygon[0][0] - polygon[n - 1][0]) *
        (polygon[0][1] + polygon[n - 1][1]);
    return sum;
}

/**
 * Determine whether a point lies inside a polygon using the
 * ray-casting (crossing number) algorithm.
 */
export function isPointInsidePolygon(
    point: ReadonlyVec2,
    polygon: ReadonlyVec2[]
): boolean {
    const n = polygon.length;
    if (n < 3) {
        return false;
    }
    const px = point[0];
    const py = point[1];
    let inside = false;
    let prevX = polygon[n - 1][0];
    let prevY = polygon[n - 1][1];
    for (let k = 0; k < n; k++) {
        const x = polygon[k][0];
        const y = polygon[k][1];
        if (y > py !== prevY > py) {
            const xCross = prevX + ((py - prevY) * (x - prevX)) / (y - prevY);
            if (xCross < px) {
                inside = !inside;
            }
        }
        prevX = x;
        prevY = y;
    }
    return inside;
}

/**
 * Normalize winding order for a set of polygons so that outer
 * contours are CW and holes (odd nesting depth) are CCW.
 *
 * Does NOT mutate the input arrays; returns new arrays with
 * vertices in the corrected order.
 *
 * The winding convention matches the SDF generator expectation:
 * CW outer contours (positive signed area) and CCW holes
 * (negative signed area).
 */
export function normalizeWindingOrder(polygons: ReadonlyVec2[][]): vec2[][] {
    if (polygons.length === 0) {
        return [];
    }

    // Step 1: Ensure every polygon is CW (positive signed area).
    // Keep track of the planar vertices for containment checks.
    const cwPolygons: vec2[][] = polygons.map((poly) => {
        const copy = poly.map((v) => vec2.fromValues(v[0], v[1]));
        if (computeSignedArea(copy) < 0) {
            copy.reverse();
        }
        return copy;
    });

    if (cwPolygons.length === 1) {
        return cwPolygons;
    }

    // Step 2: Determine nesting depth via point-in-polygon.
    // Polygons at odd nesting depth are holes and need CCW winding.
    const result: vec2[][] = cwPolygons.map((poly, i) => {
        let containmentCount = 0;
        const ref = poly[0];
        for (let j = 0; j < cwPolygons.length; j++) {
            if (i === j) {
                continue;
            }
            if (isPointInsidePolygon(ref, cwPolygons[j])) {
                containmentCount++;
            }
        }
        if (containmentCount & 1) {
            // Odd containment = hole: reverse to CCW
            const reversed = poly.slice().reverse();
            return reversed;
        }
        return poly;
    });

    return result;
}
